import {useMemo} from 'react'
import {useCategory} from '../../hooks/category/useCategory'
import {generateBreadcrumbLinkContent} from '../blocks/breadcrumb/utils'

/**
 * Custom hook to automatically generate breadcrumbs from category hierarchy.
 * Fetches category data and processes the parentCategoryTree to build a breadcrumb trail.
 *
 * @param {Object} params - Hook parameters
 * @param {boolean} params.isEnabled - Whether the hook should run (conditional execution)
 * @param {string} params.categoryId - The category ID to fetch breadcrumbs for
 * @returns {Object} Object containing categoryCrumbs array and isLoading state
 *
 * @example
 * const {categoryCrumbs, isLoading} = useAutoCategoryBreadcrumbs({
 *   isEnabled: true,
 *   categoryId: 'acne-and-skincare'
 * })
 *
 * // Returns breadcrumbs like: Home > Personal Care > Acne & Skincare
 */
export const useAutoCategoryBreadcrumbs = ({isEnabled, categoryId}) => {
    // Fetch category data using useCategory hook
    // Only runs when isEnabled is true and categoryId is provided
    const {data: categoryData, isLoading} = useCategory(
        {parameters: {id: categoryId}},
        {enabled: isEnabled && !!categoryId}
    )

    // Process parentCategoryTree to build breadcrumbs
    const autoCategoryCrumbs = useMemo(() => {
        // Return empty array if disabled or no data available
        if (!isEnabled || !categoryData?.parentCategoryTree?.length) {
            return []
        }

        // Map each category in the tree to breadcrumb format
        return categoryData.parentCategoryTree.map((category) => {
            // Create page reference data for the category
            const categoryPageData = {
                model: 'page',
                value: {
                    data: {
                        url: `/${category.id}`,
                        title: category.name
                    }
                }
            }

            // Generate Builder.io breadcrumb element
            return generateBreadcrumbLinkContent({
                title: category.name,
                isClickable: true,
                pageData: categoryPageData
            })
        })
    }, [categoryData, isEnabled])

    return {
        categoryCrumbs: autoCategoryCrumbs,
        isLoading
    }
}
